import removeMd from 'remove-markdown';
import { ResponseValueTypes } from './SurveyResponse';
import ItemEnablement from './ItemEnablement';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import truncate from 'lodash/truncate';
import ExtensionCollection from './ExtensionCollection';
import { SurveyBuilderQuestionTypes } from './SurveyBuilderQuestionTypes';
import { getFriendlyNameForUnit } from '../../../util/ucum';
import ValueMappers from '../Components/InputControls/ValueMappers';
import surveyUrls from './surveyUrls';
import QuestionnaireItemUpdater from './QuestionnaireMapper/QuestionnaireItemUpdater';
import {
  LibrarySurveyItem,
  questionnaireItemToLibraryItem,
} from './QuestionLibraryUtils';
import ValueSets from './ValueSets';
import SurveyItemAutopopulation from './SurveyItemAutopopulation';
import { TypeCheckTypes } from './fhirTypeCheck';

export type InvalidLibraryQuestionReason =
  | 'no-page-items'
  | 'no-user-selected-codes'
  | 'no-page-user-selected-codes';

export type SurveyItemTypographyVariants =
  | 'headline'
  | 'body'
  | 'subbody'
  | 'table'
  | 'caption'
  | 'button'
  | 'tooltip'
  | 'code';

/**
 * Types for the questionnaire.item[].type property
 */
export type SurveyItemTypes =
  | 'group'
  | 'display'
  | 'question'
  | 'boolean'
  | 'decimal'
  | 'integer'
  | 'date'
  | 'dateTime'
  | 'time'
  | 'string'
  | 'text'
  | 'url'
  | 'choice'
  | 'open-choice'
  | 'attachment'
  | 'reference'
  | 'quantity';

/**
 * Type for the questionnaire.item[].enableWhen[].answer[X] property
 */
export type SurveyEnablementAnswertypes =
  | 'answerBoolean'
  | 'answerDecimal'
  | 'answerInteger'
  | 'answerDate'
  | 'answerDateTime'
  | 'answerTime'
  | 'answerString'
  | 'answerUri'
  | 'answerAttachment'
  | 'answerCoding'
  | 'answerQuantity';

const controlTypes = [
  'boolean',
  'decimal',
  'integer',
  'date',
  'dateTime',
  'time',
  'string',
  'text',
  'url',
  'choice',
  'openChoice',
  'attachment',
  'reference',
  'quantity',
] as SurveyItemTypes[];

/**
 * "answer types" add an answer "answer" to the type.  This maps from that answer type to question item type
 */
export const answerTypeToTypeMap = {
  valueBoolean: 'boolean',
  valueDecimal: 'decimal',
  valueInteger: 'integer',
  valueDate: 'date',
  valueDateTime: 'dateTime',
  valueTime: 'time',
  valueString: 'string',
  valueUri: 'uri',
  valueAttachment: 'attachment',
  valueCoding: 'coding',
  valueQuantity: 'quantity',
};

export class ConfiguredOption {
  _display: string;
  _value: any;
  _responseValueType: ResponseValueTypes;
  _modifierExtensionCollection: ExtensionCollection;
  _originalResourceValue: any;
  _formValue: any;

  constructor(fhirOption: fhir.QuestionnaireItemOption) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._display = null;
    this._value = null;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._responseValueType = null;
    this._modifierExtensionCollection =
      ExtensionCollection.initFromBackboneElement(fhirOption);
    this._originalResourceValue = null;
    this._formValue = null;

    const { valueInteger, valueDate, valueTime, valueString, valueCoding } =
      fhirOption || ({} as fhir.QuestionnaireItemOption);
    if (!isNil(valueInteger)) {
      this._display = String(valueInteger);
      this._value = valueInteger;
      this._responseValueType = 'valueInteger';
      this._originalResourceValue = valueInteger;
      this._formValue = valueInteger;
    } else if (!isNil(valueDate)) {
      this._display = ValueMappers.safeFormatFhirDate(valueDate);
      this._value = valueDate;
      this._responseValueType = 'valueDate';
      this._originalResourceValue = valueDate;
      this._formValue = valueDate;
    } else if (!isNil(valueTime)) {
      this._display = ValueMappers.safeFormatFhirTime(valueTime);
      this._value = valueTime;
      this._responseValueType = 'valueTime';
      this._originalResourceValue = valueTime;
      this._formValue = valueTime;
    } else if (!isNil(valueString)) {
      this._display = valueString;
      this._value = valueString;
      this._responseValueType = 'valueString';
      this._originalResourceValue = valueString;
      this._formValue = valueString;
    } else if (!isNil(valueCoding)) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      this._display = valueCoding.display;
      this._value = valueCoding;
      this._responseValueType = 'valueCoding';
      this._originalResourceValue = valueCoding;
      this._formValue = valueCoding.code;
    }
  }

  get responseAnswerType(): ResponseValueTypes {
    return this._responseValueType;
  }

  get type(): string {
    const responseAnswerType = this.responseAnswerType;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!responseAnswerType) return null;

    return (answerTypeToTypeMap as any)[responseAnswerType] || null;
  }

  get isValid(): boolean {
    return !isNil(this._display) && !isNil(this._value);
  }

  get display(): string {
    const scaleLabel = this.scaleLabel;

    return scaleLabel === null
      ? this._display
      : `${this._display} (${scaleLabel})`;
  }

  get value(): any {
    return this._value;
  }

  get scaleLabel(): string {
    return this._modifierExtensionCollection.getValueString(
      surveyUrls.scaleLabel,
    );
  }

  get originalResourceValue(): any {
    return this._originalResourceValue;
  }

  get formValue(): any {
    return this._formValue;
  }

  get selectionType(): 'string' | 'integer' | 'coding' | 'date' | 'time' {
    if (this._responseValueType === 'valueString') {
      return 'string';
    } else if (this._responseValueType === 'valueInteger') {
      return 'integer';
    } else if (this._responseValueType === 'valueCoding') {
      return 'coding';
    } else if (this._responseValueType === 'valueDate') {
      return 'date';
    } else if (this._responseValueType === 'valueTime') {
      return 'time';
    } else {
      console.error(
        `Tried to get the selection type from an unsupported fhir type of: "${this._responseValueType}"`,
      );
      return 'string'; // default
    }
  }
}

/**
 * an immutable wrapper for a fhir questionnaire survey item
 */
export default class SurveyItem {
  /**
   * Generates a human readable version of a questionnaire item link.
   * While this is likely unique, it is not garunteed to be globally unique.  The generated value
   * should only be used for display purposes.
   * @param link
   */
  static getHumanReadableLink(link: string): string {
    if (!link || !link.length) return link;

    // ensure this is not a legacy #-# formatted link and is a guid link
    return link.length >= 36 ? link.substr(0, 4) : link;
  }

  _fhirQuestionnaireItem: fhir.QuestionnaireItem;
  _itemLink: string;
  _isRoot: boolean;
  _subItems: SurveyItem[];
  _itemEnablement: ItemEnablement;
  _modifierExtensionCollection: ExtensionCollection;
  _autopopulation: SurveyItemAutopopulation;

  constructor(
    fhirQuestionaireItem: fhir.QuestionnaireItem,
    isRoot: boolean = false,
  ) {
    this._fhirQuestionnaireItem = fhirQuestionaireItem;
    this._itemLink = get(fhirQuestionaireItem, 'linkId');
    if (!this._itemLink) {
      throw new Error(
        'Fhir Questionnaire item did not contain a linkId.  This is required.',
      );
    }

    this._isRoot = isRoot;
    const subQuestionnaireItems = get<
      fhir.QuestionnaireItem,
      'item',
      fhir.QuestionnaireItem[]
    >(fhirQuestionaireItem, 'item', []);
    this._subItems = subQuestionnaireItems.map(
      (subItem: fhir.QuestionnaireItem) => new SurveyItem(subItem, false),
    );
    this._itemEnablement = new ItemEnablement(fhirQuestionaireItem);
    this._modifierExtensionCollection =
      ExtensionCollection.initFromBackboneElement(fhirQuestionaireItem);
    this._autopopulation =
      SurveyItemAutopopulation.initFromQuestionnaireItem(fhirQuestionaireItem);
  }

  /**
   * Determines if this is a leaf item in the survey or if it has children
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get hasSubItems(): boolean {
    return this._subItems.length > 0;
  }

  /**
   * The nested survey items
   *
   * @readonly
   * @type {SurveyItem[]}
   * @memberof SurveyItem
   */
  get subItems(): SurveyItem[] {
    return this._subItems;
  }

  /**
   * Nested survey items grouped by lines to display on
   */
  get subItemsGroupedByDisplayLines(): SurveyItem[][] {
    return this.subItems.reduce((lines, surveyItem) => {
      if (!lines.length) {
        return [[surveyItem]];
      } else if (!surveyItem.isOnSameLineAsPreviousItem) {
        return [...lines, [surveyItem]];
      } else {
        const copied = [...lines];
        const lastLine = copied.pop();
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return [...copied, [...lastLine, surveyItem]];
      }
    }, [] as SurveyItem[][]);
  }

  /**
   * Does survey item contain a direct child item with the given link ID
   */
  doesContainSubItem(linkId: string): boolean {
    return !!this._subItems.find(_ => _.link === linkId);
  }

  /**
   * Is this survey item a group that will comprise of a page form section
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get isSurveyPageGroup(): boolean {
    return this.isRootItem && this.type === 'group';
  }

  /**
   * Is this survey items a root item (not nested)
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get isRootItem(): boolean {
    return this._isRoot;
  }

  /**
   * The identifier for this survey item (link is a fhir term)
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get link(): string {
    return this._itemLink;
  }

  /**
   * Returns the human readable version of this questionnaire item's link.
   * While this is likely unique, it is not garunteed to be globally unique.  This should
   * only be used for display purposes.
   */
  get humanReadableLink(): string {
    return SurveyItem.getHumanReadableLink(this.link);
  }

  /**
   * Primary text for this item
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get text(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return get(this._fhirQuestionnaireItem, 'text');
  }

  /**
   * Form input label text for this item
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get label(): string {
    const text = get(this._fhirQuestionnaireItem, 'text');
    const label = this.required ? `* ${text}` : text;

    return label || this.text;
  }

  /**
   * Does this survey item have descriptive text
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get hasText(): boolean {
    return !!this.text;
  }

  /**
   * The reason why this survey item does not qualify as a valid question library question
   */
  get invalidQuestionLibraryReason(): InvalidLibraryQuestionReason | null {
    if (this.isSurveyPageGroup) {
      if (!this.subItems?.length) {
        return 'no-page-items';
      } else if (!this.subItems.every(_ => _.hasUserSelectedCode)) {
        return 'no-page-user-selected-codes';
      }
    } else if (!this.hasUserSelectedCode) {
      return 'no-user-selected-codes';
    }

    return null;
  }

  /**
   * Does this survey item qualify as question library question
   */
  get isValidQuestionLibraryQuestion(): boolean {
    return this.invalidQuestionLibraryReason === null;
  }

  /**
   * Returns the survey item as a library item.  Returns null if survey item is invalid
   */
  get libraryItems(): LibrarySurveyItem[] | null {
    if (!this.isValidQuestionLibraryQuestion) {
      console.error(
        'tried to get a library survey item from an invalid survey item',
      );
      return null;
    }

    if (this.isSurveyPageGroup) {
      return this.subItems.map(_ =>
        questionnaireItemToLibraryItem(_.fhirQuestionnaireItem),
      );
    } else {
      return [questionnaireItemToLibraryItem(this.fhirQuestionnaireItem)];
    }
  }

  /**
   * The code that will be assigned to generate survey response data
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get code(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return (
      (this._fhirQuestionnaireItem?.code || []).find(_ => !_?.userSelected)
        ?.code || null
    );
  }

  /**
   * The code specifically configured by the user.  This will often refer to a valid
   * system's code an not a randomly generated guid.  This is leveraged for properly codifying
   * downstream generated data
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get userSelectedCode(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return (
      (this._fhirQuestionnaireItem?.code || []).find(_ => _?.userSelected)
        ?.code || null
    );
  }

  /**
   * Was a code explicitely configured for this survey item
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get hasUserSelectedCode(): boolean {
    return this.userSelectedCode !== null;
  }

  /**
   * After versioning this survey, are edits to this survey item forbidden?
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get disableVersionedEdits(): boolean {
    /*
      The goal of versioning surveys is to keep the derived (observation) data consistent across survey version.
      Initially, any survey item that wasn't a page group was locked down to prevent future edits,
      with the philosophy that if it was unnecessarily restrictive, it would be easier to lock down less in the future
      than to lock down more. However, this has been in place for a few years now and editing versioned surveys can be
      incredibly frustrating. Often the entire question has to be deleted and recreated just to make a minor adjustment.
      New philosophy: Allow as much to be edited as possible and if we find a specific situation or question type
      where it should be locked down, then we can update the code to handle that specific situation.
    */
    return (
      !this.isSurveyPageGroup &&
      this.surveyBuilderQuestionType === 'lifeology-course' // this is largely a stand in
    );
  }

  /**
   * Gets the item's tex in a human readable format.  Will strip markdown and perform truncation if needed
   *
   * @param truncationCutoff - The max amount of characters allowed in the text before the string is truncated
   */
  getFriendlyDisplayText(truncationCutoff: number = 100): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.hasText) return null;

    const withoutMarkdown = this.markdownDisplay
      ? removeMd(this.text)
      : this.text;

    return truncate(withoutMarkdown, {
      length: truncationCutoff,
    });
  }

  /**
   * Evaluates if the comparison survey item is a valid "version" of this survey item or if the survey
   * item is technically/conceptually a new type of survey item
   * @param comparisonItem - the prospective item from a different version of the survey
   */
  isVersionOfItem(comparisonItem: SurveyItem): boolean {
    if (!comparisonItem) {
      return false;
    }

    // either this item or comp item must have updates disabled to have been a "verioned" survey item
    if (!this.isUpdateForbidden && !comparisonItem.isUpdateForbidden) {
      return false;
    }

    // not an exhaustive list, but safe to assume if these properties are equal, it is the same survey item
    return (
      this.link === comparisonItem.link && // cannot be a copied version if links are different
      this.text === comparisonItem.text && // altered text could change the meaning of the question
      this.type === comparisonItem.type && // altered type would indicate a structural difference in the question
      this.code === comparisonItem.code // altered code would completely fork the downstream data
    );
  }

  /**
   * Prefix text for this item
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get prefix(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return get(this._fhirQuestionnaireItem, 'prefix');
  }

  /**
   * Does this survey item have prefix text
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get hasPrefix(): boolean {
    return !!this.prefix;
  }

  /**
   * The type of the item
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get type(): SurveyItemTypes {
    return get(this._fhirQuestionnaireItem, 'type') as SurveyItemTypes;
  }

  /**
   * Does this survey type represent an input control
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get isControl(): boolean {
    return controlTypes.includes(this.type);
  }

  /**
   * Is this item required
   *
   * @readonly
   * @returns {boolean}
   * @memberof SurveyItem
   */
  get required(): boolean {
    return get(this._fhirQuestionnaireItem, 'required', false);
  }

  /**
   * Is there a maximum length restriction on the input
   *
   * @readonly
   * @returns {boolean}
   * @memberof SurveyItem
   */
  get hasMaxLengthRestriction(): boolean {
    return this.maxLength !== null;
  }

  /**
   * The maximum length restriction on the input
   *
   * @readonly
   * @returns {number}
   * @memberof SurveyItem
   */
  get maxLength(): number {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return get(this._fhirQuestionnaireItem, 'maxLength', null);
  }

  /**
   * Does this allow for repeat items
   *
   * @readonly
   * @returns {boolean}
   * @memberof SurveyItem
   */
  get repeats(): boolean {
    return get(this._fhirQuestionnaireItem, 'repeats', false);
  }

  /**
   * The option reference list url
   *
   * @readonly
   * @type {string}
   * @memberof SurveyItem
   */
  get optionReference(): string {
    return get(this._fhirQuestionnaireItem, 'options.reference');
  }

  /**
   * Is option reference present on this survey item
   */
  get hasOptionReference(): boolean {
    return !!this.optionReference;
  }

  /**
   * The configured quantity metadata (unit, system, etc)
   */
  get referenceQuantity(): fhir.Quantity {
    return this._modifierExtensionCollection.getValueQuantity(
      surveyUrls.referenceQuantity,
    );
  }

  /**
   * The explicitely configured options
   *
   * @readonly
   * @type {ConfiguredOption[]}
   * @memberof SurveyItem
   */
  get optionList(): ConfiguredOption[] {
    const options = get<
      fhir.QuestionnaireItem,
      'option',
      fhir.QuestionnaireItemOption[]
    >(this._fhirQuestionnaireItem, 'option', []);
    return options
      .map(
        (fhirOption: fhir.QuestionnaireItemOption) =>
          new ConfiguredOption(fhirOption),
      )
      .filter((configuredOption: ConfiguredOption) => configuredOption.isValid);
  }

  get hasConfiguredOptions(): boolean {
    return this.optionList.length > 0;
  }

  /**
   * Is this survey item choice-based (open-choice or choice types)
   */
  get isChoiceBasedType(): boolean {
    return this.type === 'choice' || this.type === 'open-choice';
  }

  /**
   * If this is a choice survey item, determines the type of the response value as a choice can represent a few different types
   *
   * @readonly
   * @type {ResponseValueTypes}
   * @memberof SurveyItem
   */
  get choiceAnswerType(): ResponseValueTypes {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.isChoiceBasedType || !this.hasConfiguredOptions) return null;

    // it is assumed all choices are of the same type
    return this.optionList[0].responseAnswerType;
  }

  /**
   * If this is a choice survey item, determines the type of the response value as a choice can represent a few different types
   *
   * @readonly
   * @type {ResponseValueTypes}
   * @memberof SurveyItem
   */
  get choicetype(): ResponseValueTypes {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.isChoiceBasedType || !this.hasConfiguredOptions) return null;

    // it is assumed all choices are of the same type
    return this.optionList[0].responseAnswerType;
  }

  /**
   * The item enablment logic for this item type
   *
   * @readonly
   * @type {ItemEnablement}
   * @memberof SurveyItem
   */
  get itemEnablement(): ItemEnablement {
    return this._itemEnablement;
  }

  /**
   * Is this a group of radio button table items
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get isTableGroup(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.tableGroup,
    );
  }

  /**
   * The unit label to display instead of the standard UCUM generated label
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get unitLabel(): string | null {
    return (
      this._modifierExtensionCollection.getValueString(surveyUrls.unitLabel) ||
      null
    );
  }

  /**
   * Is summing child scored answers
   */
  get isSumScoring(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.sumScoreSection,
    );
  }

  /**
   * Is averaging child scored answers
   */
  get isAverageScoring(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.avgScoreSection,
    );
  }

  /**
   * Does this survey item score descendent survey items
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get isScoring(): boolean {
    return this.isSumScoring || this.isAverageScoring;
  }

  /**
   * Should this survey item's response not count towards any scoring
   */
  get isMarkedForScoringOmission() {
    return this._modifierExtensionCollection.hasExtension(surveyUrls.omitScore);
  }

  /**
   * Should this survey item be rendered as a radio button group.  This is determined by the extension and question type
   *
   * @readonly
   * @type {boolean}
   * @memberof SurveyItem
   */
  get isRadioButtonGroup(): boolean {
    return (
      this._modifierExtensionCollection.hasExtension(
        surveyUrls.radioButtonGroup,
      ) &&
      (this.type === 'choice' || this.type === 'open-choice')
    );
  }

  get isIndented(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.indentation,
    );
  }

  get indentationCount(): number {
    return this._modifierExtensionCollection.getValueInteger(
      surveyUrls.indentation,
    );
  }

  get hasDenseMargins(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.denseMargins,
    );
  }

  get isOnSameLineAsPreviousItem(): boolean {
    return this._modifierExtensionCollection.hasExtension(surveyUrls.sameLine);
  }

  /**
   * Does non-negative validation apply
   */
  get nonNegative(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.nonNegative,
    );
  }

  /**
   * Should display as markdown
   */
  get markdownDisplay(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.markdownDisplay,
    );
  }

  /**
   * Is this survey item the root of a question made with the survey builder
   */
  get isSurveyBuilderQuestion(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.builderQuestionType,
    );
  }

  /**
   * Does this survey item specify the permitted precision of the value
   */
  get hasSpecifiedValuePrecision(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.valuePrecision,
    );
  }

  /**
   * Does this survey item specify the permitted precision of the value
   */
  get valuePrecision(): number {
    return this._modifierExtensionCollection.getValueInteger(
      surveyUrls.valuePrecision,
    );
  }

  /**
   * Is this survey item the root of a question made with the survey builder
   */
  get surveyBuilderQuestionType(): SurveyBuilderQuestionTypes {
    return this._modifierExtensionCollection.getValueString(
      surveyUrls.builderQuestionType,
    ) as SurveyBuilderQuestionTypes;
  }

  /**
   * The original fhir questionnaire item this survey item wraps
   */
  get fhirQuestionnaireItem(): fhir.QuestionnaireItem {
    return this._fhirQuestionnaireItem;
  }

  /**
   * Are updates to this questionnaire items forbiden.  This could be because the item is published.
   */
  get isUpdateForbidden(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.updatesForbidden,
    );
  }

  /**
   * If a lifeology course url (modifier extension) was saved, this will retrieve it.
   */
  get lifeologyCourseUrl(): string {
    return this._modifierExtensionCollection.getValueUri(
      surveyUrls.lifeologyCourseUrl,
    );
  }

  /**
   * Returns the variant the MUI typography variant the element should be displayed as.  Returns null if none specified
   */
  get displayTypographyVariant(): SurveyItemTypographyVariants | null {
    const rawValue = this._modifierExtensionCollection.getValueString(
      surveyUrls.displayTypographyVariant,
    );

    if (!rawValue) return null;

    const allVariants: SurveyItemTypographyVariants[] = [
      'headline',
      'body',
      'subbody',
      'table',
      'caption',
      'button',
      'tooltip',
      'code',
    ];

    return allVariants.includes(rawValue as SurveyItemTypographyVariants)
      ? (rawValue as SurveyItemTypographyVariants)
      : 'subbody';
  }

  get hasMinNumericBoundsValidation(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.minNumericBounds,
    );
  }

  get minNumericBounds(): number {
    return this._modifierExtensionCollection.getValueDecimal(
      surveyUrls.minNumericBounds,
    );
  }

  get hasMaxNumericBoundsValidation(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.maxNumericBounds,
    );
  }

  get maxNumericBounds(): number {
    return this._modifierExtensionCollection.getValueDecimal(
      surveyUrls.maxNumericBounds,
    );
  }

  get hasMinSelectionCountBoundsValidation(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.minSelectionCount,
    );
  }

  get minSelectionCountBounds(): number {
    return this._modifierExtensionCollection.getValueInteger(
      surveyUrls.minSelectionCount,
    );
  }

  get hasMaxSelectionCountBoundsValidation(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.maxSelectionCount,
    );
  }

  get maxSelectionCountBounds(): number {
    return this._modifierExtensionCollection.getValueInteger(
      surveyUrls.maxSelectionCount,
    );
  }

  get hasMinCharCountValidation(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.minCharCount,
    );
  }

  get minCharCountBounds(): number {
    return this._modifierExtensionCollection.getValueInteger(
      surveyUrls.minCharCount,
    );
  }

  get hasMaxCharCountValidation(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.maxCharCount,
    );
  }

  get maxCharCountBounds(): number {
    return this._modifierExtensionCollection.getValueInteger(
      surveyUrls.maxCharCount,
    );
  }

  get imageId(): string {
    return this._modifierExtensionCollection.getValueString(surveyUrls.imageId);
  }

  get isCheckpointPage(): boolean {
    const hasCheckpointPageExtension =
      this._modifierExtensionCollection.hasExtension(surveyUrls.checkpointPage);

    if (!this.isSurveyPageGroup && hasCheckpointPageExtension) {
      console.error(
        `A survey item with link "${this.link}" had a checkpoint page extension but is not a survey page.  This will be ignored.`,
      );
      return false;
    }

    return hasCheckpointPageExtension;
  }

  get hasAutopopulationConfigured(): boolean {
    return this._autopopulation.isValid;
  }

  get autopopulation(): SurveyItemAutopopulation {
    return this._autopopulation;
  }

  /**
   * the unit friendly name for this survey item
   */
  get unitDisplay(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (this.type !== 'quantity') return null;

    const refUnit =
      (this.initialValue as fhir.Quantity) || this.referenceQuantity;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const code = get<fhir.Quantity, 'code', string>(refUnit, 'code', null);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
    return getFriendlyNameForUnit(code);
  }

  /**
   * When mapping from form field, addition metadata might need to be provided
   */
  get mappingInitialValue(): fhir.Quantity {
    // other types than quantity can be supported eventually
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (this.type !== 'quantity') return null;

    return this.referenceQuantity;
  }

  /**
   * Should this matrix table survey item always be displayed as a table instead of a radio button list
   */
  get shouldForceMatrixTableVariant(): boolean {
    return this._modifierExtensionCollection.hasExtension(
      surveyUrls.forceMatrixTable,
    );
  }

  /**
   * What the response type will the item have.  This evaluates the type based on the simple type property or by what option types a choice
   * question contains.
   *
   * @readonly
   * @type {string} - One of the Questionnaire response answer types or null if not applicable (is a group items for example)
   * @memberof SurveyItem
   */
  get responseValueType(): ResponseValueTypes {
    switch (this.type) {
      case 'boolean':
        return 'valueBoolean';
      case 'decimal':
        return 'valueDecimal';
      case 'quantity':
        return 'valueQuantity';
      case 'integer':
        return 'valueInteger';
      case 'date':
        return 'valueDate';
      case 'dateTime':
        return 'valueDateTime';
      case 'time':
        return 'valueTime';
      case 'string':
        return 'valueString';
      case 'text':
        return 'valueString';
      case 'url':
        return 'valueUri';
      case 'attachment':
        return 'valueAttachment';
      case 'choice':
      case 'open-choice':
        if (this.surveyBuilderQuestionType === 'genomics-report') {
          return 'valueCoding';
        } else if (this.optionReference) {
          return 'valueCoding';
        } else {
          return this.choiceAnswerType;
        }
      default:
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return null;
    }
  }

  get typeCheckType(): TypeCheckTypes | null {
    switch (this.type) {
      case 'boolean':
      case 'decimal':
      case 'quantity':
      case 'integer':
      case 'date':
      case 'dateTime':
      case 'time':
      case 'string':
      case 'attachment':
        return this.type;
      case 'text':
        return 'string';
      case 'url':
        return 'string';
      case 'choice':
      case 'open-choice':
        if (this.optionReference) {
          return 'coding';
        } else {
          switch (this.responseValueType) {
            case 'valueBoolean':
              return 'boolean';
            case 'valueDecimal':
              return 'decimal';
            case 'valueInteger':
              return 'integer';
            case 'valueDate':
              return 'date';
            case 'valueDateTime':
              return 'dateTime';
            case 'valueTime':
              return 'time';
            case 'valueString':
              return 'string';
            case 'valueUri':
              return 'string';
            case 'valueAttachment':
              return 'attachment';
            case 'valueCoding':
              return 'coding';
            case 'valueQuantity':
              return 'quantity';
            default:
              return null;
          }
        }
      default:
        return null;
    }
  }

  /**
   * What will the enablement answer type be of this item if referenced
   *
   * @readonly
   * @type {string} - One of the Questionnaire enablement answer types or null if not applicable (is a group items for example)
   * @memberof SurveyItem
   */
  get enablementAnswerType(): SurveyEnablementAnswertypes {
    switch (this.type) {
      case 'boolean':
        return 'answerBoolean';
      case 'decimal':
        return 'answerDecimal';
      case 'quantity':
        return 'answerQuantity';
      case 'integer':
        return 'answerInteger';
      case 'date':
        return 'answerDate';
      case 'dateTime':
        return 'answerDateTime';
      case 'time':
        return 'answerTime';
      case 'string':
        return 'answerString';
      case 'text':
        return 'answerString';
      case 'url':
        if (this.surveyBuilderQuestionType === 'lifeology-course') {
          return 'answerDecimal';
        }
        return 'answerUri';
      case 'attachment':
        return 'answerAttachment';
      case 'choice':
      case 'open-choice':
        if (this.optionReference) {
          return 'answerCoding';
        } else {
          switch (this.choiceAnswerType) {
            case 'valueString':
              return 'answerString';
            case 'valueInteger':
              return 'answerInteger';
            case 'valueDate':
              return 'answerDate';
            case 'valueTime':
              return 'answerTime';
            default:
              console.error(
                `Unrecognized choice answer type of "${this.choiceAnswerType}", defaulting to string`,
              );
              return 'answerString';
          }
        }
      default:
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return null;
    }
  }

  get initialValue(): any {
    const initialValueProperties = [
      'initialBoolean',
      'initialDecimal',
      'initialInteger',
      'initialDate',
      'initialDateTime',
      'initialTime',
      'initialString',
      'initialUri',
      'initialAttachment',
      'initialCoding',
      'initialQuantity',
    ];

    const targetProperty = initialValueProperties.find(initialValueProperty => {
      return (
        (this._fhirQuestionnaireItem as any)[initialValueProperty] !== undefined
      );
    });

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return (this._fhirQuestionnaireItem as any)[targetProperty];
  }

  get hasInitialValue(): boolean {
    return this.initialValue !== undefined;
  }

  /**
   * If importing this question from another survey, this generates an importable versoin of the item by:
   * removing any enablement rules
   * removing any extensions related to enablement
   * adding the "updates forbidden" extension
   */
  get importedVersionOfItem(): fhir.QuestionnaireItem {
    return new QuestionnaireItemUpdater(this._fhirQuestionnaireItem)
      .clearEnablementRules()
      .clearOrRules()
      .setUpdatesForbidden().questionnaireItem;
  }

  /**
   * Generate the survey item score given the response
   */
  getScoringValue(
    surveyValueSets: ValueSets,
    answers: fhir.QuestionnaireResponseItemAnswer[],
  ): number[] {
    if (!answers || this.isMarkedForScoringOmission) return [];

    // lifeology courses are not scored
    if (this.surveyBuilderQuestionType === 'lifeology-course') return [];

    const getSingleAnswerValue = (
      answer: fhir.QuestionnaireResponseItemAnswer,
    ): number | null => {
      if (!answer) return null;

      // numeric values require no special mapping
      const numericAnswerValue = [
        answer?.valueDecimal,
        answer?.valueInteger,
        answer?.valueQuantity?.value,
      ].find(numericValue => !isNil(numericValue));

      if (!isNil(numericAnswerValue)) return numericAnswerValue;

      // coding answer types can have a score if there is an explicit numeric value configured
      if (!answer?.valueCoding?.code || !this.optionReference) return null;

      const targetValueSet = surveyValueSets.getValueSetWithId(
        this.optionReference,
      );

      if (!targetValueSet) return null;

      return targetValueSet.getScoringValue(answer.valueCoding);
    };

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return (answers || []).map(getSingleAnswerValue).filter(_ => !isNil(_));
  }

  get evidenceLevelValues(): fhir.Coding[] {
    return this._modifierExtensionCollection.getValueCodeableConcept(
      surveyUrls.evidenceLevelValues,
    );
  }
}
